import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import ApiHelper from 'api-helper';
import Toast from '@common/helpers/toast';
import { Paginate, PaginateInput } from '@/models/paginate';

export interface MessageTemplate {
  id: number;
  title: string;
  content: string;
  display_order: number | null;
  is_active: boolean;
  updated_by: string | null;
  updated_at: string;
}

const DEFAULT_PER_PAGE = 20;

@Module({ name: 'messageTemplate', namespaced: true })
export default class MessageTemplateStore extends VuexModule {
  public list: MessageTemplate[] = [];
  public isLoading = false;
  public paginate: Paginate = new Paginate().deserialize({ per_page: DEFAULT_PER_PAGE });

  public chatList: MessageTemplate[] = [];
  public isChatListLoading = false;

  get activeList() {
    return this.list.filter((item) => item.is_active);
  }

  get activeChatList() {
    return this.chatList.filter((item) => item.is_active);
  }

  @Action({ rawError: true })
  public async fetchList(params: { page?: number; per_page?: number } = {}) {
    const { commit } = this.context;
    try {
      commit('updateIsLoading', true);
      const response = await ApiHelper.getApi('MessageTemplateApi').getList({
        page: params.page || this.paginate.current_page,
        per_page: params.per_page || this.paginate.per_page,
      });
      commit('SET_LIST', response);
    } catch (error) {
      Toast.error(error?.response?.data?.message || 'エラーが発生しました');
    } finally {
      commit('updateIsLoading', false);
    }
  }

  @Action({ rawError: true })
  public async fetchChatList() {
    const { commit } = this.context;
    try {
      commit('updateIsChatListLoading', true);
      const response = await ApiHelper.getApi('MessageTemplateApi').getList({});
      commit('SET_CHAT_LIST', response);
    } catch (error) {
      // No.176: this is a passive background prefetch mounted app-wide
      // (MessageInputModal.vue in App.vue) for the chat template picker.
      // Callers already gate it with canUseMessageTemplate(), but a role
      // without access (clinic_admin/cs/pharmacist) must never see an error
      // toast just from the app loading — fail silently here instead.
    } finally {
      commit('updateIsChatListLoading', false);
    }
  }

  @Action({ rawError: true })
  public async createTemplate(params: { title: string; content: string; display_order?: number }) {
    const result = await ApiHelper.getApi('MessageTemplateApi').create(params);
    await this.context.dispatch('fetchList');
    await this.context.dispatch('fetchChatList');
    return result;
  }

  @Action({ rawError: true })
  public async updateTemplate(payload: { id: number; data: Partial<MessageTemplate> }) {
    const result = await ApiHelper.getApi('MessageTemplateApi').update(payload.id, payload.data);
    await this.context.dispatch('fetchList');
    await this.context.dispatch('fetchChatList');
    return result;
  }

  @Action({ rawError: true })
  public async deleteTemplate(id: number) {
    const result = await ApiHelper.getApi('MessageTemplateApi').destroy(id, {});
    const isLastItemOnPage = this.list.length === 1 && this.paginate.current_page > 1;
    await this.context.dispatch('fetchList', {
      page: isLastItemOnPage ? this.paginate.current_page - 1 : this.paginate.current_page,
    });
    await this.context.dispatch('fetchChatList');
    return result;
  }

  @Mutation
  public SET_LIST(response: { data: MessageTemplate[] } & Partial<PaginateInput>) {
    this.list = response?.data || [];
    this.paginate = new Paginate().deserialize(response);
  }

  @Mutation
  public SET_CHAT_LIST(response: { data: MessageTemplate[] } & Partial<PaginateInput>) {
    this.chatList = response?.data || [];
  }

  @Mutation
  public updateIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @Mutation
  public updateIsChatListLoading(isLoading: boolean) {
    this.isChatListLoading = isLoading;
  }
}
